import React from "react";
import {autobind} from "core-decorators";
import {computed} from "mobx";
import {inject, observer} from "mobx-react";
import {Button, message, Tooltip} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import _isEqual from "lodash/isEqual";

import "./Manage.scss";
import {createPortalSession} from "@/lib/backend";
import {paths} from "@/routes";
import loadChargebee, {getChargebeeInstance} from "@/lib/chargebee/loadChargebee";
import Store from "@/stores";

export interface ISubscriptionManagementProps {
  store?: Store;
}

@loadChargebee
@inject("store")
@autobind
@observer
export default class SubscriptionManagement extends React.Component<ISubscriptionManagementProps> {
  refreshSessionDataTimeout?: any;

  @computed({keepAlive: true})
  get chargebee() {
    const {stageStore} = this.props.store!;
    return getChargebeeInstance(stageStore.stage.chargebeeSite);
  }

  async openPortal() {
    this.chargebee.setPortalSession(async () => {
      try {
        return await createPortalSession();
      } catch (error) {
        const errorMessage = error?.toJSON?.()?.error ?? "Unknown error";
        message.error(`Error opening subscription management screen: ${errorMessage}`);
      }
    });

    const chargebeePortalInstance = this.chargebee.createChargebeePortal();
    // https://www.chargebee.com/checkout-portal-docs/api.html#open
    chargebeePortalInstance.open({
      close: () => {
        this.refreshSessionData();
      },
      // subscriptionCancelled: () => {
      //   // This happens when user cancels the subscription, but probably won't happen with our
      //   // current Chargebee configuration which is that subscription are cancelled at the end of
      //   // the billing period.
      // },
      // subscriptionChanged: () => {
      // },
      // subscriptionPaused: function () {
      //   // Cannot actually happen if 'pause subscriptions' is disabled [?]
      // },
      // subscriptionResumed: function () {
      //   // Cannot actually happen if 'pause subscriptions' is disabled [?]
      // },
      // scheduledPauseRemoved: function () {
      //   // Cannot actually happen if 'pause subscriptions' is disabled [?]
      // },
      // subscriptionReactivated: function () {
      //   // Cannot actually happen if 'pause subscriptions' is disabled [?]
      // },
    });
  }

  // Keep fetching session data until the subscription data has changed
  // We use this to detect when the subscription changes that the user has done in Chargebee (through
  // the Chargbee Portal) are propagated to are database with the webhook
  // We setup a maxium number of retires since the user might have opened the Chargee Portal and changed nothing
  // Also, the timeout grows exponenitally for each retry
  // Instead of this, we might keep the subcription data always in sync with a Hasura subscription query
  async refreshSessionData(retries = 10, timeout = 1000) {
    if (this.refreshSessionDataTimeout) {
      clearTimeout(this.refreshSessionDataTimeout);
      this.refreshSessionDataTimeout = undefined;
    }

    if (retries === 0) return;

    const {session} = this.props.store!;
    const {sessionData} = session;
    await session.fetchSessionData();
    const newSessionData = session.sessionData;

    // Retry 1 second later if subscription has not changed
    if (_isEqual(sessionData, newSessionData))
      this.refreshSessionDataTimeout = setTimeout(
        async () => this.refreshSessionData(retries - 1, timeout * 2),
        timeout,
      );
  }

  render() {
    return (
      <div className="Manage">
        <Button type="primary" onClick={this.openPortal}>
          Manage subscription
        </Button>
        <p className="manage-header">Click Manage Subscription above to:</p>
        <ul className="subscription-management-actions">
          <li>View the details of your subscription</li>
          <li>
            Upgrade your current plan{" "}
            <Tooltip title="Open instructions" placement="right">
              <a href={paths.manual("57ffd04be9514aecb314023b2225cde0")} target="_blank" rel="help noreferrer">
                <QuestionCircleOutlined />
              </a>
            </Tooltip>
          </li>
          <li>View your billing history and download invoices</li>
          <li>Change your payment method or billing country</li>
          <li>
            Cancel your subscription (note: the subscription fee is non-refundable. When you cancel your subscription it
            will remain active until the end of the current subscription term.){" "}
            <Tooltip title="Open instructions" placement="right">
              <a href={paths.manual("039c57c9cd5a4acb91cbb9ba67513668")} target="_blank" rel="help noreferrer">
                <QuestionCircleOutlined />
              </a>
            </Tooltip>
          </li>
        </ul>
      </div>
    );
  }
}
